import type { Logger} from '@bayer-int/portletce-shared';
import { fetchExtensions } from '@bayer-int/portletce-shared';

import type {
    ErrorMonitoringService} from 'portlet/common/services';
import {
    HttpService,
    AuthService,
    CacheService,
    BWIntegration,
    APIGatewayIntegration,
    DatabricksProxyApiIntegration,
    CloudWatchRUMErrorMonitoringService,
    MockErrorMonitoringService,
} from 'portlet/common/services';
import type { Config } from 'portlet/models';
import {
    VegaSpecificationService,
    SpecificationService,
    MasterDataService,
    SettingService,
    MailingService,
    AutoDateService,
    InteractionToFilterService,
    TelemetryService,
    ThemeService,
    SessionStorageService,
} from 'portlet/services';
import { HierarchyVariantsService } from 'portlet/services/HierarchyVariantsService';

import type { Boot } from './boot';
import {
    isLocal,
    isProd,
    oauthBasicCredentials,
    azureClientId,
    azureTentant,
    useMockAuth,
    redirectUriForRedirectFlow,
    gumsUsersApiOrigin,
    gumsUsersApiScope,
    gumsUsersApiSubscriptionKey,
    hrUnoApiOriginDev,
    hrUnoApiOriginQas,
    hrUnoApiOriginPrd,
    hrHccApiOriginDev,
    hrHccApiOriginQas,
    hrHccApiOriginPrd,
    hrDeiApiOriginDev,
    hrEmployeeListApiOriginDev,
    hrUnoApiScopeDev,
    hrUnoApiScopeQas,
    hrUnoApiScopePrd,
    hrHccApiScopeDev,
    hrHccApiScopeQas,
    hrHccApiScopePrd,
    hrDeiApiScopeDev,
    hrEmployeeListApiScopeDev,
    hrUnoApiSubscriptionKeyDev,
    hrUnoApiSubscriptionKeyQas,
    hrUnoApiSubscriptionKeyPrd,
    hrHccApiSubscriptionKeyDev,
    hrHccApiSubscriptionKeyQas,
    hrHccApiSubscriptionKeyPrd,
    hrDeiApiSubscriptionKeyDev,
    hrEmployeeListApiSubscriptionKeyDev,
    azureClientIdProd,
    telemetryDevEndpoint,
    databricksProxyApiBaseUrlDev,
    databricksProxyApiScopeDev,
    databricksProxyApiBaseUrlPrd,
    databricksProxyApiScopePrd,
    packageVersion,
    cloudWatchRUM,
} from './env';

const applyDefaultCache = fetchExtensions.cache();

export const buildRootService = (boot: Boot, config: Config, logger: Logger) => {
    const isDev = !isProd || config.env === 'dev';
    const clientId = isDev ? azureClientId : azureClientIdProd;
    const cloudWatchRUMConfig = isDev ? cloudWatchRUM.dev : cloudWatchRUM.prod;
    const cachedFetch = config.fetchcache
        ? fetchExtensions.cache(config.fetchcache)(fetch)
        : applyDefaultCache(fetch);

    const authService = AuthService.configure(
        {
            noAuth: config.noauth,
            location,
            useLocalBackend: isLocal,
            useMockAuth: !!useMockAuth,
            basicAuthCredentials: oauthBasicCredentials,
            tokenDomainWhitelist: [boot.servers.oauthServer.url],
            accessTokenDomainWhitelist: [boot.servers.graphApiServer.url],
            tentant: azureTentant,
            clientId,
            fetch: cachedFetch,
            // for auth to work on safari (in general when popups are blocked)
            // we need to provide the full PUBLIC_PATH to the app, and register it in azure portal
            // otherwise we will be redirected to the origin (404 on nginx)
            // on the other hand, providing the redirectUri seems to break popup flow,
            // so we use it only when we do the redirects (`redirectUri` vs `redirectUriForRedirectFlow`)
            redirectUriForRedirectFlow,
            ...boot.authConfig,
        },
        [
            new BWIntegration(boot),
            APIGatewayIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                apiOrigin: gumsUsersApiOrigin,
                apiScope: gumsUsersApiScope,
                subscriptionKey: gumsUsersApiSubscriptionKey,
            }),
            APIGatewayIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                apiOrigin: hrUnoApiOriginDev,
                apiScope: hrUnoApiScopeDev,
                subscriptionKey: hrUnoApiSubscriptionKeyDev,
            }),
            APIGatewayIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                apiOrigin: hrUnoApiOriginQas,
                apiScope: hrUnoApiScopeQas,
                subscriptionKey: hrUnoApiSubscriptionKeyQas,
            }),
            APIGatewayIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                apiOrigin: hrUnoApiOriginPrd,
                apiScope: hrUnoApiScopePrd,
                subscriptionKey: hrUnoApiSubscriptionKeyPrd,
            }),
            APIGatewayIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                apiOrigin: hrHccApiOriginDev,
                apiScope: hrHccApiScopeDev,
                subscriptionKey: hrHccApiSubscriptionKeyDev,
            }),
            APIGatewayIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                apiOrigin: hrHccApiOriginQas,
                apiScope: hrHccApiScopeQas,
                subscriptionKey: hrHccApiSubscriptionKeyQas,
            }),
            APIGatewayIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                apiOrigin: hrHccApiOriginPrd,
                apiScope: hrHccApiScopePrd,
                subscriptionKey: hrHccApiSubscriptionKeyPrd,
            }),
            APIGatewayIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                apiOrigin: hrDeiApiOriginDev,
                apiScope: hrDeiApiScopeDev,
                subscriptionKey: hrDeiApiSubscriptionKeyDev,
            }),
            APIGatewayIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                apiOrigin: hrEmployeeListApiOriginDev,
                apiScope: hrEmployeeListApiScopeDev,
                subscriptionKey: hrEmployeeListApiSubscriptionKeyDev,
                allowedMethod: 'post',
            }),
            DatabricksProxyApiIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                databricksProxyApiBaseUrl: databricksProxyApiBaseUrlDev,
                databricksProxyApiScope: databricksProxyApiScopeDev,
            }),
            DatabricksProxyApiIntegration.configure({
                clientId,
                tentant: azureTentant,
                redirectUriForRedirectFlow,
                databricksProxyApiBaseUrl: databricksProxyApiBaseUrlPrd,
                databricksProxyApiScope: databricksProxyApiScopePrd,
            }),
        ],
    );
    const _httpService = new HttpService(cachedFetch);
    const authHttpService = new HttpService(authService.asFetch());
    const specService = new SpecificationService(authHttpService, boot, config.versionstatus);
    const vegaSpecService = new VegaSpecificationService(authHttpService, boot);
    const hierarchyVariantsService = new HierarchyVariantsService(authHttpService, boot);
    const masterDataService = new MasterDataService(
        authHttpService,
        hierarchyVariantsService,
        boot,
    );
    const cache = new CacheService();
    const settingService = new SettingService(masterDataService, cache);
    const mailingService = new MailingService(authHttpService, boot);
    const autoDateService = new AutoDateService();
    const interactionService = new InteractionToFilterService(masterDataService);
    const telemetryService = new TelemetryService(authHttpService, telemetryDevEndpoint);
    const errorMonitoringService: ErrorMonitoringService = config.disableerrormonitoring
        ? new MockErrorMonitoringService()
        : new CloudWatchRUMErrorMonitoringService(
              cloudWatchRUMConfig.appId,
              cloudWatchRUMConfig.region,
              packageVersion,
              config.env,
              cloudWatchRUMConfig.guestRoleArn,
              cloudWatchRUMConfig.identityPool,
          );
    const themeService = new ThemeService(authHttpService, boot.servers.themesServer.url);
    const sessionStorageService = new SessionStorageService();

    return {
        logger,
        boot,
        authService,
        specService,
        cache,
        vegaSpecService,
        settingService,
        mailingService,
        autoDateService,
        interactionService,
        telemetryService,
        errorMonitoringService,
        themeService,
        sessionStorageService,
    };
};

export type RootService = ReturnType<typeof buildRootService>;
